import { useEffect, useMemo, useRef } from "react"
import { useViewer } from "common/hooks/data/useViewer"
import { useViewerSubscriptions } from "common/hooks/data/useViewerSubscriptions"
import { useSubscribedNewslettersContext } from "modules/newsletters/contexts"
import { useNewsletterGroups } from "modules/newsletters/hooks/useNewsletterGroups"
import { useTranslations } from "modules/i18n/hooks/useTranslations"
import { NewsletterSubscriber } from "../NewsletterSubscriber"
import { EmailInputSection } from "../EmailInputSection"
import { PrivacyPolicyFooter } from "../PrivacyPolicyFooter"
import { DisclosureNotice } from "../DisclosureNotice"
import { Group, GroupItem } from "common/types/graphql"
import { NewslettersV2Provider } from "modules/newsletters-v2/contexts"
import clsx from "classnames"
import { trackNewsletterLandingPageViewed } from "@theconversation/shared-components/braze-tc"

/**
 * Main component for the newsletters-v2 page.
 * This component combines the data fetching logic from the current newsletters page
 * with a modern UI inspired by the marcus-playground version.
 */
export const NewslettersMain = () => {
  const { groups, promotedNewsletter } = useNewsletterGroups()
  const { isSignedIn, loaded } = useViewer()
  const { getNewsletterSubscriptions } = useViewerSubscriptions()
  const {
    setAllIds,
    ids: subscribedNewsletterIds,
    toggle: toggleContext,
  } = useSubscribedNewslettersContext()
  const hasInitialized = useRef(false)
  const t = useTranslations("newsletters")

  // Reorganize groups, moving the promoted newsletter if any to the first position, then create rank map
  const { reorganizedGroups, allNewsletters, rankMap } = useMemo(() => {
    const reorganizedGroups: Group[] = groups
      .map((group) => ({
        ...group,
        groupItems: group.groupItems.filter((item) => item.id !== promotedNewsletter?.id),
      }))
      .map((group, groupIndex) => {
        if (groupIndex === 0 && promotedNewsletter) {
          return {
            ...group,
            groupItems: [promotedNewsletter, ...group.groupItems],
          }
        }
        return group
      })

    // Build newsletters array and assign ranks sequentially after reorganization
    const allNewsletters: GroupItem[] = []
    const rankMap = new Map<string, number>()
    let currentRank = 1

    reorganizedGroups.forEach(({ groupItems }) => {
      groupItems.forEach((groupItem) => {
        allNewsletters.push(groupItem)
        rankMap.set(groupItem.id, currentRank)
        currentRank++
      })
    })

    return {
      reorganizedGroups,
      allNewsletters,
      rankMap,
    }
  }, [groups, promotedNewsletter])

  // Pre-select first newsletter for anonymous users
  useEffect(() => {
    if (hasInitialized.current || isSignedIn || allNewsletters.length === 0) {
      return
    }

    // Select the first newsletter (which will be the promoted one if it exists)
    const defaultNewsletter = allNewsletters[0]
    if (defaultNewsletter && !subscribedNewsletterIds.has(defaultNewsletter.id)) {
      toggleContext(defaultNewsletter.id)
    }

    hasInitialized.current = true
  }, [isSignedIn, allNewsletters, subscribedNewsletterIds, toggleContext])

  // Load existing subscriptions for signed-in users
  useEffect(() => {
    // We don't want to change the context state if the user is not signed in, as it might cause a race condition
    // with the newsletter subscription context state, unselecting newsletters that the user has selected (before
    // fetching the `viewer`'s GraphQL data).
    if (!isSignedIn) return

    const newsletterSubscriptionIds = getNewsletterSubscriptions().map(
      ({ newsletterList: { id } }) => id,
    )
    setAllIds(newsletterSubscriptionIds)
  }, [isSignedIn, getNewsletterSubscriptions, setAllIds])

  // Send a Braze tracking event
  useEffect(() => {
    if (loaded) trackNewsletterLandingPageViewed()
  }, [loaded])

  if (!reorganizedGroups.length) {
    return <div className="min-h-screen bg-white" />
  }

  // Wait for viewer status to be known before rendering
  if (!loaded) {
    return <div className="min-h-screen bg-white" />
  }

  return (
    <NewslettersV2Provider>
      <div className="min-h-screen bg-white">
        <div className="mx-auto flex max-w-4xl flex-col px-1 pt-12 pb-6 sm:px-6">
          <h1 className="mb-4 text-center font-serif text-4xl">{t("v2.heading.page_title")}</h1>
          <h2 className="sr-only">{t("v2.heading.sub_title_aria_description")}</h2>
          {/* Email Input or Success Message */}
          {!isSignedIn && <EmailInputSection />}

          {/* Signed-in sub-header */}
          {isSignedIn && (
            <div className="mx-auto max-w-xl text-center">
              <p className="font-serif text-lg leading-relaxed">{t("v2.signed_in_sub_header")}</p>
            </div>
          )}

          {/* Newsletter Lists - Grouped with headers */}
          <div className="mx-auto w-full max-w-3xl flex-col py-8 sm:gap-8 sm:px-8 sm:pt-12">
            <fieldset className="space-y-4">
              <legend className="sr-only">Newsletters</legend>
              {reorganizedGroups.map(({ name, groupItems }, groupIndex) => {
                const isFirstGroup = groupIndex === 0

                return (
                  <section key={name} className={clsx({ "mt-12": !isFirstGroup })}>
                    {!isFirstGroup && (
                      <h2 className={clsx("font-sans-heading mt-16 mb-3 text-lg font-bold")}>
                        {name}
                      </h2>
                    )}
                    <div className="space-y-4">
                      {groupItems.map((newsletter) => (
                        <NewsletterSubscriber
                          key={newsletter.id}
                          newsletter={newsletter}
                          rank={rankMap.get(newsletter.id) ?? 0}
                        />
                      ))}
                    </div>
                  </section>
                )
              })}
            </fieldset>
          </div>

          {/* Privacy Policy Footer */}
          <PrivacyPolicyFooter />

          {/* reCAPTCHA Privacy Notice, which will only be shown to anonymous users */}
          {!isSignedIn && (
            <DisclosureNotice className="mx-auto mt-4 max-w-2xl text-center text-xs text-gray-600" />
          )}
        </div>
      </div>
    </NewslettersV2Provider>
  )
}
